/**
 * IdleKillerConnect — Engine #28 v35.17 AWS connection wizard.
 *
 * Two-stage setup:
 *   Stage 1: Customer creates a READ-ONLY IAM role in their AWS account
 *            using the policy JSON we generate, attaches it to a trust
 *            policy pinned to CARTIE's master account + their unique
 *            ExternalId. They paste the role ARN here. We try AssumeRole.
 *
 *   Stage 2: Customer (later, when they're ready) creates a SEPARATE
 *            destructive role using the destructive policy JSON. Attaches
 *            same trust policy. Pastes that ARN here too. Now and only
 *            now can they graduate any destructive policy.
 *
 * Until stage 2 is done, CARTIE is physically incapable of deleting
 * anything in the customer's AWS account.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Copy, ShieldCheck, ShieldOff, CheckCircle2, AlertTriangle, ExternalLink, Cloud, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/** Normalize Axios error → human string. FastAPI 422 returns
 * detail as List[{type,loc,msg,...}]; everything else is a string. */
const formatErr = (e, fallback = 'Request failed') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x && typeof x === 'object' ? (x.msg || JSON.stringify(x)) : String(x))).join('; ');
  }
  if (typeof d === 'object') return d.msg || JSON.stringify(d);
  return String(d);
};

const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error('Copy failed'),
  );
};

export default function IdleKillerConnect() {
  const [policies, setPolicies] = useState(null);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readonlyArn, setReadonlyArn] = useState('');
  const [destructiveArn, setDestructiveArn] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/idle-killer/cloud-connect/policies`, auth()),
        axios.get(`${API}/idle-killer/cloud-connect`, auth()),
      ]);
      setPolicies(p.data);
      setState(s.data);
      setReadonlyArn(s.data.readonly_role_arn || '');
      setDestructiveArn(s.data.destructive_role_arn || '');
    } catch (e) {
      toast.error(formatErr(e, 'Failed to load IAM setup'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveReadonly = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/idle-killer/cloud-connect/readonly`,
        { role_arn: readonlyArn.trim() }, auth());
      toast.success('Read-only role saved');
      load();
    } catch (e) {
      toast.error(formatErr(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const saveDestructive = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/idle-killer/cloud-connect/destructive`,
        { role_arn: destructiveArn.trim() }, auth());
      toast.success('Cleanup role saved');
      load();
    } catch (e) {
      toast.error(formatErr(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const revokeDestructive = async () => {
    try {
      await axios.delete(`${API}/idle-killer/cloud-connect/destructive`, auth());
      toast.success('Cleanup role revoked. CARTIE can no longer remove anything in your AWS.');
      load();
    } catch (e) { toast.error(formatErr(e, 'Revoke failed')); }
  };

  const verifyReadonly = async () => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/idle-killer/cloud-connect/verify`, {}, auth());
      if (data.ok) {
        toast.success(`Verified — assumed role in AWS account ${data.account_id}`);
      } else {
        toast.error(`Verify failed: ${data.error || 'unknown'}`);
      }
      load();
    } catch (e) {
      toast.error(formatErr(e, 'Verify failed'));
    } finally { setVerifying(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center">Loading…</div>;
  }

  const hasReadonly = !!state?.readonly_role_arn;
  const hasDestructive = !!state?.destructive_role_arn;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="idle-killer-connect-page">
      <Helmet><title>Connect AWS · Idle Resource Optimizer · CARTIEAI</title></Helmet>

      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/idle-killer" className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 text-sm" data-testid="back-to-idle-killer">
            <ArrowLeft className="w-4 h-4" /> Idle-killer
          </Link>
          <div className="text-xs text-slate-500 font-mono">/idle-killer/connect · v35.17</div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-2xl bg-slate-900 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-900 ring-1 ring-emerald-500/30 p-6 sm:p-8" data-testid="connect-hero">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-3">
            <Cloud className="w-3 h-3" /> Connect AWS · Read-only first, destructive separately
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="connect-h1">
            Two roles. <span className="text-emerald-400">Zero risk by default.</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl leading-relaxed">
            CARTIE uses two completely separate IAM roles to access your AWS account. The read-only role can only discover idle resources. The cleanup role is what authorizes removal. You attach them in two stages — and you can revoke the cleanup role at any time without breaking discovery.
          </p>

          <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="status-tiles">
            <div className={`rounded-xl bg-slate-950 ring-1 p-4 ${hasReadonly ? 'ring-emerald-500/40' : 'ring-amber-500/40'}`}>
              <div className="flex items-center gap-2">
                {hasReadonly ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                <span className="text-[10px] uppercase tracking-wider font-mono text-slate-300">Stage 1 · Read-only</span>
              </div>
              <div className={`text-sm font-mono mt-2 ${hasReadonly ? 'text-emerald-200' : 'text-amber-200'}`} data-testid="status-readonly">
                {hasReadonly ? 'Connected — discovery active' : 'Not connected — using demo data'}
              </div>
            </div>
            <div className={`rounded-xl bg-slate-950 ring-1 p-4 ${hasDestructive ? 'ring-emerald-500/40' : 'ring-slate-700/40'}`}>
              <div className="flex items-center gap-2">
                {hasDestructive ? <ShieldOff className="w-4 h-4 text-emerald-400" /> : <ShieldCheck className="w-4 h-4 text-emerald-400" />}
                <span className="text-[10px] uppercase tracking-wider font-mono text-slate-300">Stage 2 · Cleanup</span>
              </div>
              <div className={`text-sm font-mono mt-2 ${hasDestructive ? 'text-emerald-200' : 'text-slate-300'}`} data-testid="status-destructive">
                {hasDestructive ? 'Connected — cleanup enabled' : 'Not connected — we cannot remove anything'}
              </div>
            </div>
          </div>
        </div>

        {/* The shared ExternalId + master account */}
        <Card className="bg-slate-900 border-white/10" data-testid="external-id-card">
          <CardContent className="p-5 space-y-3">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Lock className="w-5 h-5 text-emerald-400" /> Your unique ExternalId
            </h2>
            <p className="text-sm text-slate-400">
              Used in the trust policy of BOTH roles below. Pins the role to your account so no other AWS principal can assume it.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-sm bg-slate-950 ring-1 ring-white/10 rounded px-3 py-2 text-emerald-300" data-testid="external-id-value">
                {policies?.external_id || '—'}
              </code>
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(policies?.external_id, 'ExternalId')} data-testid="copy-external-id">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>
            <div className="text-xs text-slate-400 mt-2">
              CARTIE master account ID: <code className="font-mono text-slate-200">{policies?.cartie_master_account_id || 'REPLACE_WITH_CARTIE_ACCOUNT_ID'}</code>
            </div>
          </CardContent>
        </Card>

        {/* Stage 1 — Read-only role */}
        <Card className="bg-slate-900 border-white/10" data-testid="readonly-section">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" /> Stage 1: Read-only role
              </h2>
              {hasReadonly && <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 text-[10px]">Connected</Badge>}
            </div>

            <ol className="text-sm text-slate-300 space-y-2 list-decimal pl-5">
              <li>
                In your AWS console, go to <a className="text-emerald-300 hover:underline" href="https://console.aws.amazon.com/iam/home?#/policies" target="_blank" rel="noreferrer">IAM → Policies → Create policy</a>, paste the JSON below, and name it <code className="font-mono bg-slate-950 px-1.5 py-0.5 rounded">CartieIdleKillerReadOnlyPolicy</code>.
              </li>
              <li>
                Then create a new role (IAM → Roles → Create role → Custom trust policy). Paste the <strong>trust policy</strong> below. Attach the policy from step 1. Name the role <code className="font-mono bg-slate-950 px-1.5 py-0.5 rounded">CartieIdleKillerReadOnly</code>.
              </li>
              <li>Paste the role ARN here.</li>
            </ol>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1 flex items-center justify-between">
                  <span>Read-only policy JSON</span>
                  <button onClick={() => copyToClipboard(policies?.readonly_policy_json, 'Read-only policy')} className="text-emerald-400 hover:text-emerald-300 normal-case font-sans flex items-center gap-1" data-testid="copy-readonly-policy">
                    <Copy className="w-3 h-3" /> copy
                  </button>
                </div>
                <Textarea readOnly value={policies?.readonly_policy_json || ''} className="bg-slate-950 ring-1 ring-emerald-500/20 border-0 text-emerald-200 font-mono text-[10px] leading-tight h-48" data-testid="readonly-policy-json" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1 flex items-center justify-between">
                  <span>Trust policy JSON (same for both roles)</span>
                  <button onClick={() => copyToClipboard(policies?.trust_policy_json, 'Trust policy')} className="text-emerald-400 hover:text-emerald-300 normal-case font-sans flex items-center gap-1" data-testid="copy-trust-policy">
                    <Copy className="w-3 h-3" /> copy
                  </button>
                </div>
                <Textarea readOnly value={policies?.trust_policy_json || ''} className="bg-slate-950 ring-1 ring-emerald-500/20 border-0 text-emerald-200 font-mono text-[10px] leading-tight h-48" data-testid="trust-policy-json" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Read-only role ARN</label>
              <div className="flex gap-2 flex-wrap">
                <Input
                  value={readonlyArn}
                  onChange={(e) => setReadonlyArn(e.target.value)}
                  placeholder="arn:aws:iam::123456789012:role/CartieIdleKillerReadOnly"
                  className="bg-slate-950 ring-1 ring-white/10 border-0 text-slate-100 font-mono flex-1 min-w-[240px]"
                  data-testid="input-readonly-arn"
                />
                <Button onClick={saveReadonly} disabled={saving || !readonlyArn.trim()} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="save-readonly-btn">
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                {hasReadonly && (
                  <Button onClick={verifyReadonly} disabled={verifying} variant="outline" data-testid="verify-readonly-btn">
                    {verifying ? 'Verifying...' : 'Verify (try AssumeRole)'}
                  </Button>
                )}
              </div>
              {state?.last_verified_account_id && (
                <div className="text-xs text-emerald-300 mt-2 font-mono" data-testid="last-verified">
                  ✓ Last verified — AWS account {state.last_verified_account_id}
                  {state.last_verified_at && ` (${new Date(state.last_verified_at).toLocaleString()})`}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stage 2 — Cleanup role */}
        <Card className={`bg-slate-900 border-white/10 ${!hasReadonly ? 'opacity-60' : ''}`} data-testid="destructive-section">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                {hasDestructive ? <ShieldOff className="w-5 h-5 text-emerald-400" /> : <ShieldCheck className="w-5 h-5 text-emerald-400" />}
                Stage 2: Cleanup role <span className="text-xs font-normal text-slate-400">(optional, do when ready)</span>
              </h2>
              {hasDestructive && <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 text-[10px]">Connected — cleanup enabled</Badge>}
            </div>

            <div className="rounded-lg bg-amber-500/5 ring-1 ring-amber-500/20 p-3 text-xs text-amber-200" data-testid="destructive-warning">
              <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Attaching this role lets CARTIE perform cleanups after you click through the 5 safety gates per resource. You can revoke it at any time below; the read-only role keeps working for discovery.
            </div>

            {!hasReadonly && (
              <div className="text-xs text-slate-500 italic">Complete Stage 1 first.</div>
            )}

            {hasReadonly && (
              <>
                <ol className="text-sm text-slate-300 space-y-2 list-decimal pl-5">
                  <li>Create another policy in IAM → Policies → Create policy with the cleanup JSON below. Name it <code className="font-mono bg-slate-950 px-1.5 py-0.5 rounded">CartieIdleKillerCleanupPolicy</code>.</li>
                  <li>Create a SEPARATE role (different name!) <code className="font-mono bg-slate-950 px-1.5 py-0.5 rounded">CartieIdleKillerCleanup</code>. Use the SAME trust policy from Stage 1. Attach the cleanup policy.</li>
                  <li>Paste the new role ARN here.</li>
                </ol>

                <div>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1 flex items-center justify-between">
                    <span>Cleanup policy JSON</span>
                    <button onClick={() => copyToClipboard(policies?.destructive_policy_json, 'Cleanup policy')} className="text-amber-400 hover:text-amber-300 normal-case font-sans flex items-center gap-1" data-testid="copy-destructive-policy">
                      <Copy className="w-3 h-3" /> copy
                    </button>
                  </div>
                  <Textarea readOnly value={policies?.destructive_policy_json || ''} className="bg-slate-950 ring-1 ring-amber-500/20 border-0 text-amber-200 font-mono text-[10px] leading-tight h-40" data-testid="destructive-policy-json" />
                </div>

                <div>
                  <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Cleanup role ARN</label>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      value={destructiveArn}
                      onChange={(e) => setDestructiveArn(e.target.value)}
                      placeholder="arn:aws:iam::123456789012:role/CartieIdleKillerCleanup"
                      className="bg-slate-950 ring-1 ring-white/10 border-0 text-slate-100 font-mono flex-1 min-w-[240px]"
                      data-testid="input-destructive-arn"
                    />
                    <Button onClick={saveDestructive} disabled={saving || !destructiveArn.trim()} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="save-destructive-btn">
                      {saving ? 'Saving...' : 'Save cleanup role'}
                    </Button>
                    {hasDestructive && (
                      <Button onClick={revokeDestructive} variant="outline" className="text-amber-400 border-amber-500/40" data-testid="revoke-destructive-btn">
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="rounded-xl bg-slate-900 ring-1 ring-emerald-500/20 p-4 text-xs text-slate-300" data-testid="invariant-footer">
          <strong className="text-emerald-300">The headline invariant.</strong> Without a cleanup role ARN registered above, the server refuses to activate any cleanup policy. The read-only role is restricted server-side to a fixed allowlist of {policies?.readonly_actions?.length || '~14'} Describe* + GetMetric* actions — see the JSON above. No code path in our codebase calls a cleanup AWS API without an active cleanup session. <Link to="/idle-killer" className="text-emerald-300 hover:text-emerald-200 underline">Back to the Idle Resource Optimizer</Link>.
        </div>
      </main>
    </div>
  );
}
